import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import AdminPage from '../../components/admin/AdminPage';
import Loader from '../../components/common/Loader';
import NoData from '../../components/common/NoData';
import {
  addGenericSurveyQuestion,
  deleteGenericSurveyQuestion,
  createGenericSurvey,
  getGenericSurvey,
  publishGenericSurvey,
  updateGenericSurvey,
  updateGenericSurveyQuestion,
  updateContentOrder,
} from '../../services/adminService';
import { debounce } from 'lodash';

export const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

function SurveyBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new';
  const surveyId = isNew ? null : Number(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishChoice, setShowPublishChoice] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [baselineQuestionsCount, setBaselineQuestionsCount] = useState(0);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [collectName, setCollectName] = useState(false);
  const [collectEmail, setCollectEmail] = useState(false);

  const [baseline, setBaseline] = useState({ title: '', description: '', isPublic: false, collectName: false, collectEmail: false });

  const [activeQid, setActiveQid] = useState(null); // null | number
  const [questionError, setQuestionError] = useState('');
  const [qDraft, setQDraft] = useState({
    type: 'single_choice',
    question_text: '',
    is_required: false,
    options: ['Option 1', 'Option 2'],
  });

  const qDraftRef = useRef(qDraft);
  const surveyIdRef = useRef(surveyId);
  const activeQidRef = useRef(activeQid);

  useEffect(() => { qDraftRef.current = qDraft; }, [qDraft]);
  useEffect(() => { surveyIdRef.current = surveyId; }, [surveyId]);
  useEffect(() => { activeQidRef.current = activeQid; }, [activeQid]);

  const shareUrl = useMemo(() => {
    if (!survey?.share_slug) return '';
    try {
      return `${window.location.origin}/survey/${survey.share_slug}`;
    } catch {
      return '';
    }
  }, [survey?.share_slug]);

  const fetchSurvey = useCallback(async (silent = false) => {
    if (isNew) {
      setSurvey({ status: 'draft' });
      setQuestions([]);
      setLoading(false);
      return;
    }
    if (!surveyId) {
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const resp = await getGenericSurvey(surveyId);
      if (resp?.success) {
        const s = resp.data?.survey;
        setSurvey(s);
        const qs = resp.data?.questions || [];
        setQuestions(qs);
        setBaselineQuestionsCount(qs.length);
        setTitle(s?.title || '');
        setDescription(s?.description || '');
        setIsPublic(Boolean(s?.is_public));
        setCollectName(Boolean(s?.collect_name));
        setCollectEmail(Boolean(s?.collect_email));
        setBaseline({
          title: s?.title || '',
          description: s?.description || '',
          isPublic: Boolean(s?.is_public),
          collectName: Boolean(s?.collect_name),
          collectEmail: Boolean(s?.collect_email),
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [surveyId, isNew]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const toDraftFromQuestion = (q) => {
    const type = q?.type || 'single_choice';
    const options = (q?.options || []).map((o) => o.option_text).filter(Boolean);
    return {
      type,
      question_text: q?.question_text || '',
      is_required: Boolean(q?.is_required),
      options: type === 'text' ? [] : (options.length ? options : ['Option 1', 'Option 2']),
    };
  };

  const normalizeOptions = (opts) => {
    return (Array.isArray(opts) ? opts : [])
      .map((s) => String(s ?? '').trim())
      .filter(Boolean);
  };

  const saveActiveQuestionImmediately = async () => {
    const qid = activeQidRef.current;
    if (!qid || String(qid).startsWith('temp-')) return;
    const draft = qDraftRef.current;
    const payload = {
      question_text: String(draft.question_text || '').trim(),
      type: draft.type,
      is_required: draft.is_required ? 1 : 0,
      options: draft.type === 'text' ? [] : normalizeOptions(draft.options),
    };
    try {
      await updateGenericSurveyQuestion(surveyIdRef.current, qid, payload);
    } catch (e) {}
  };

  const openNewQuestion = () => {
    // If a question is already open, sync its changes first
    if (activeQid) syncDraftToLocalQuestions();
    
    setActiveQid(null);
    setQuestionError('');
    
    const tempId = `temp-${Date.now()}`;
    const newQ = {
      id: tempId,
      question_text: '', // Empty as per user request to use placeholders
      type: 'single_choice',
      is_required: 0,
      options: [{ option_text: '' }, { option_text: '' }],
      is_local: true,
    };
    
    setQuestions(prev => [...prev, newQ]);
    setActiveQid(tempId);
    setQDraft({
      type: 'single_choice',
      question_text: '',
      is_required: false,
      options: ['', '']
    });
  };

  const syncDraftToLocalQuestions = () => {
    if (!activeQid) return;
    setQuestions(prev => prev.map(q => {
      if (q.id === activeQid) {
        return {
          ...q,
          question_text: qDraft.question_text,
          type: qDraft.type,
          is_required: qDraft.is_required,
          options: qDraft.type === 'text' ? [] : qDraft.options.map(opt => ({ option_text: opt }))
        };
      }
      return q;
    }));
  };

  const openExistingQuestion = (qid) => {
    if (activeQid === qid) return;
    if (activeQid) syncDraftToLocalQuestions();
    
    const q = questions.find(x => x.id === qid);
    if (q) {
      setActiveQid(qid);
      setQDraft(toDraftFromQuestion(q));
    }
  };

  const isSurveyDirty = useMemo(() => {
    const t = String(title || '').trim();
    const bt = String(baseline.title || '').trim();
    const d = String(description || '');
    const bd = String(baseline.description || '');
    
    // Check main details
    if (t !== bt || d !== bd || 
        Boolean(isPublic) !== Boolean(baseline.isPublic) ||
        Boolean(collectName) !== Boolean(baseline.collectName) ||
        Boolean(collectEmail) !== Boolean(baseline.collectEmail)) {
      return true;
    }

    // Check if questions count changed
    if (questions.length !== baselineQuestionsCount) return true;

    // Check if any temp items exist
    if (questions.some(q => String(q.id).startsWith('temp-'))) return true;

    return false;
  }, [title, description, isPublic, collectName, collectEmail, baseline, questions, baselineQuestionsCount]);

  const handleSaveSurvey = async () => {
    if (!String(title || '').trim()) {
      toast.error('Title is required');
      return;
    }
    
    // 1. Sync current editor first
    if (activeQid) {
       syncDraftToLocalQuestions();
    }

    setSaving(true);
    try {
      // 2. Save Survey Detail
      const payload = {
        title: String(title).trim(),
        description,
        is_public: isPublic ? 1 : 0,
        collect_name: collectName ? 1 : 0,
        collect_email: collectEmail ? 1 : 0,
      };
      
      const resp = isNew
        ? await createGenericSurvey(payload)
        : await updateGenericSurvey(surveyId, payload);
        
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to save survey details');
        setSaving(false);
        return;
      }

      const currentSurveyId = isNew ? resp.data.id : surveyId;

      // 3. Parallel Question Saving
      const questionPromises = questions.map(q => {
        const isNewQ = String(q.id).startsWith('temp-');
        const qPayload = {
          question_text: q.question_text || 'Untitled Question',
          type: q.type,
          is_required: q.is_required ? 1 : 0,
          options: q.type === 'text' ? [] : (q.options || []).map(o => o.option_text).filter(Boolean),
        };

        if (isNewQ) {
          return addGenericSurveyQuestion(currentSurveyId, qPayload);
        } else {
          return updateGenericSurveyQuestion(currentSurveyId, q.id, qPayload);
        }
      });

      await Promise.all(questionPromises);

      // Automatic ordering sync removed as per user request
      
      toast.success(isNew ? 'Survey created' : 'All changes saved');
      
      if (isNew) {
        navigate(`/admin/surveys/${currentSurveyId}`, { replace: true });
      } else {
        await fetchSurvey();
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to save some changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setShowPublishChoice(true);
  };

  const doPublish = async (nextIsPublic) => {
    setPublishing(true);
    try {
      const saveResp = await updateGenericSurvey(surveyId, {
        title: String(title || '').trim(),
        description,
        is_public: nextIsPublic ? 1 : 0,
        collect_name: collectName ? 1 : 0,
        collect_email: collectEmail ? 1 : 0,
      });
      if (!saveResp?.success) {
        toast.error(saveResp?.message || 'Failed to save publish settings');
        return;
      }
      const resp = await publishGenericSurvey(surveyId);
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to publish');
        return;
      }
      toast.success('Published');
      setIsPublic(Boolean(nextIsPublic));
      await fetchSurvey();
      setShowPublishChoice(false);
    } catch (e) {
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveDraftQuestion = async (manual = false) => {
    // No longer used for autosave. 
    // We'll keep it as a local sync for now.
    syncDraftToLocalQuestions();
    if (manual) toast.success('Changes applied locally. Click "Save changes" at the top to persist.');
  };

  const handleCloseEditor = () => {
    if (activeQid) syncDraftToLocalQuestions();
    setActiveQid(null);
  };

  const handleDeleteQuestion = async (qid) => {
    if (String(qid).startsWith('temp-')) {
      setQuestions((prev) => prev.filter((q) => q.id !== qid));
      if (activeQid === qid) {
        setActiveQid(null);
        setQuestionError('');
      }
      return;
    }
    try {
      const resp = await deleteGenericSurveyQuestion(surveyId, qid);
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to delete question');
        return;
      }
      toast.success('Question deleted');
      if (activeQid === qid) {
        setActiveQid(null);
        setQuestionError('');
      }
      await fetchSurvey();
    } catch (e) {
      toast.error('Failed to delete question');
    }
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setQuestions(items);
    // Automatic ordering API call removed as per user request
  };

  if (loading) {
    return (
      <AdminPage
        back={
          <button
            type="button"
            onClick={() => navigate('/admin/surveys')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back
          </button>
        }
        title="Survey"
      >
        <Loader message="Loading survey..." />
      </AdminPage>
    );
  }

  if (!survey) {
    return (
      <AdminPage
        back={
          <button
            type="button"
            onClick={() => navigate('/admin/surveys')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back
          </button>
        }
        title="Survey"
      >
        <NoData message="Survey not found" />
      </AdminPage>
    );
  }

  return (
    <AdminPage noPadding>
      <div className="w-full max-w-[1440px] mx-auto px-6 pt-0 pb-12 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-8 border-b border-gray-100 gap-6">
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => navigate('/admin/surveys')}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 font-bold text-sm"
            >
              <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
              Back to surveys
            </button>
            <div className="flex items-center gap-2 text-[#7083FF] font-bold text-xs uppercase tracking-[0.2em] mt-3">
              <Icon icon="fluent:compose-24-regular" width={16} />
              <span>Survey Builder</span>
            </div>
            <h1 className="text-4xl font-black text-gray-900 tracking-tight truncate">{title || 'Untitled Survey'}</h1>
            <p className="text-gray-500 font-medium max-w-2xl">Create questions, publish, and share/assign surveys.</p>
          </div>

          <div className="flex flex-col items-start md:items-end gap-2">
            <button
              type="button"
              onClick={handleSaveSurvey}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#7083FF] text-white px-4 py-3 text-sm font-black hover:brightness-95 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving
                </>
              ) : (
                <>
                  <Icon icon="fluent:save-24-regular" width="18" height="18" />
                  Save changes
                </>
              )}
            </button>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              {isPublic && survey.status === 'published' && survey.share_slug ? (
                <div className="flex items-center gap-2 sm:justify-end">
                  <div
                    className="text-xs font-semibold text-gray-700 truncate max-w-[220px] sm:max-w-[240px] md:max-w-[320px]"
                    title={shareUrl}
                  >
                    {shareUrl}
                  </div>
                  <button
                    type="button"
                    onClick={copyShare}
                    className="inline-flex items-center gap-2 rounded-2xl bg-white border border-gray-100 px-3 py-2 text-xs font-black text-gray-700 hover:bg-gray-50 shadow-sm"
                    title="Copy link"
                  >
                    <Icon icon="fluent:copy-24-regular" width="16" height="16" className="text-[#7083FF]" />
                    Copy
                  </button>
                </div>
              ) : null}

              <button
                type="button"
                onClick={handlePublish}
                disabled={publishing || isNew}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm font-black text-gray-700 hover:bg-gray-50 disabled:opacity-60 min-w-[120px]"
              >
                {publishing ? (
                  <>
                    <span className="w-4 h-4 border-2 border-gray-800 border-t-transparent rounded-full animate-spin" />
                    Updating
                  </>
                ) : survey.status === 'published' ? (
                  isPublic ? (
                    <>
                      <Icon icon="fluent:globe-24-regular" width="18" height="18" className="text-[#7083FF]" />
                      Public
                    </>
                  ) : (
                    <>
                      <Icon icon="fluent:people-24-regular" width="18" height="18" className="text-[#7083FF]" />
                      Internal
                    </>
                  )
                ) : (
                  <>
                    <Icon icon="fluent:globe-24-regular" width="18" height="18" className="text-[#7083FF]" />
                    Publish
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate(`/admin/surveys/${surveyId}/analytics`)}
                disabled={isNew}
                className="inline-flex items-center gap-2 rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-60"
              >
                <Icon icon="fluent:chart-multiple-24-regular" width="18" height="18" className="text-[#7083FF]" />
                Analytics
              </button>

              <button
                type="button"
                onClick={() => navigate(`/admin/surveys/${surveyId}/assign`)}
                disabled={isNew}
                className="inline-flex items-center gap-2 rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-60"
              >
                <Icon icon="fluent:people-24-regular" width="18" height="18" className="text-[#7083FF]" />
                Assign
              </button>
            </div>
          </div>
        </div>

        {showPublishChoice ? (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-gray-900">Publish survey</div>
                  <div className="text-xs text-gray-500 mt-0.5">Choose who can access this survey</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPublishChoice(false)}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-50"
                  disabled={publishing}
                >
                  <Icon icon="fluent:dismiss-24-regular" width="18" height="18" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <button
                  type="button"
                  onClick={() => doPublish(false)}
                  disabled={publishing}
                  className="w-full text-left p-4 rounded-2xl border border-gray-100 bg-white hover:bg-gray-50 disabled:opacity-60"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gray-100 flex items-center justify-center">
                      <Icon icon="fluent:people-24-regular" width="20" height="20" className="text-gray-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-black text-gray-900">Internal</div>
                      <div className="text-xs text-gray-500 mt-0.5">Only assigned trainees can fill (no public link)</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => doPublish(true)}
                  disabled={publishing}
                  className="w-full text-left p-4 rounded-2xl border border-gray-100 bg-white hover:bg-gray-50 disabled:opacity-60"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center">
                      <Icon icon="fluent:globe-24-regular" width="20" height="20" className="text-indigo-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-black text-gray-900">Public</div>
                      <div className="text-xs text-gray-500 mt-0.5">Anyone with the link can fill (link will be generated)</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="space-y-5">
          <div className="bg-white rounded-[28px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-black text-gray-900">Survey details</div>
                <div className="text-xs text-gray-500 mt-0.5">Update title, description and sharing settings</div>
              </div>
              <div className="text-xs font-black px-3 py-1.5 rounded-2xl border border-gray-100 bg-gray-50 text-gray-700">
                {String(survey.status || 'draft').toUpperCase()}
              </div>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-black text-gray-700 uppercase tracking-[0.18em]">Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-3 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:bg-white"
                    placeholder="Survey title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-700 uppercase tracking-[0.18em]">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-3 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:bg-white"
                    rows={4}
                    placeholder="Short description (optional)"
                  />
                </div>

                <div className="rounded-2xl border border-gray-100 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="text-sm font-black text-gray-900">Collect respondent details</div>
                    <div className="text-xs text-gray-500 mt-0.5">Show name/email fields on the public survey (optional)</div>
                  </div>
                  <div className="flex items-center gap-5">
                    <label className="inline-flex items-center gap-2 text-sm font-black text-gray-700">
                      <span>Name</span>
                      <span className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={collectName}
                          onChange={(e) => setCollectName(e.target.checked)}
                          className="sr-only"
                        />
                        <span className={`w-9 h-5 rounded-full transition-colors ${collectName ? 'bg-[#7083FF]' : 'bg-gray-200'}`} />
                        <span className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${collectName ? 'translate-x-4' : 'translate-x-0'}`} />
                      </span>
                    </label>

                    <label className="inline-flex items-center gap-2 text-sm font-black text-gray-700">
                      <span>Email</span>
                      <span className="relative inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={collectEmail}
                          onChange={(e) => setCollectEmail(e.target.checked)}
                          className="sr-only"
                        />
                        <span className={`w-9 h-5 rounded-full transition-colors ${collectEmail ? 'bg-[#7083FF]' : 'bg-gray-200'}`} />
                        <span className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${collectEmail ? 'translate-x-4' : 'translate-x-0'}`} />
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[28px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-black text-gray-900">Questions</div>
                <div className="text-xs text-gray-500 mt-0.5">Click a question to edit it inline</div>
              </div>
            </div>

            <div className="p-5 space-y-3">
              {questions.length === 0 ? (
                <NoData message="No questions yet" />
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <StrictModeDroppable droppableId="questions">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                        {questions.map((q, idx) => {
                          const isActive = activeQid === q.id;
                          return (
                            <Draggable key={q.id} draggableId={String(q.id)} index={idx}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`rounded-[24px] border overflow-hidden ${isActive ? 'border-[#7083FF]/15 bg-[#7083FF]/5' : 'border-gray-100 bg-white'
                                    }`}
                                >
                                  <div
                                    type="button"
                                    onClick={async () => {
                                      if (isActive) {
                                        await handleCloseEditor();
                                        return;
                                      }
                                      await openExistingQuestion(q.id);
                                    }}
                                    className="w-full px-5 py-4 text-left flex items-start justify-between gap-4 cursor-pointer"
                                  >
                                    <div className="flex items-start gap-4 min-w-0">
                                      <div {...provided.dragHandleProps} className="mt-1 text-gray-300 hover:text-gray-500 transition-colors">
                                        <Icon icon="fluent:line-horizontal-3-24-regular" width="20" />
                                      </div>
                                      <div className="min-w-0">
                                        <div className="text-xs font-black text-gray-500">Q{idx + 1}</div>
                                        <div className="text-sm font-black text-gray-900 truncate">{q.question_text || 'Untitled Question'}</div>
                                        <div className="mt-1 text-xs text-gray-500 font-medium truncate">
                                          {q.type.replace('_', ' ')}
                                          {q.is_required ? ' • required' : ''}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="shrink-0 flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteQuestion(q.id);
                                        }}
                                        className="w-10 h-10 rounded-2xl border border-red-100 bg-white text-red-500 hover:bg-red-50 flex items-center justify-center"
                                        title="Delete"
                                      >
                                        <Icon icon="fluent:delete-24-regular" width={18} height={18} />
                                      </button>
                                      <div className="w-10 h-10 rounded-2xl border border-gray-100 bg-white text-gray-400 flex items-center justify-center">
                                        <Icon icon={isActive ? 'fluent:chevron-up-24-regular' : 'fluent:chevron-down-24-regular'} width={18} height={18} />
                                      </div>
                                    </div>
                                  </div>

                                  {isActive ? (
                                    <>
                                      {questionError ? (
                                        <div className="px-5 py-3 bg-gray-900 text-white flex items-center gap-3">
                                          <Icon icon="fluent:warning-24-filled" width={18} height={18} className="text-amber-300" />
                                          <div className="text-sm font-semibold">{questionError}</div>
                                        </div>
                                      ) : null}

                                      <div className="p-5 bg-white">
                                        <div className="flex flex-col sm:flex-row gap-3 items-start">
                                          <div className="flex-1 w-full">
                                            <label className="block text-xs font-black text-gray-700 uppercase tracking-[0.18em]">Question</label>
                                            <input
                                              value={qDraft.question_text}
                                              onChange={(e) => setQDraft((p) => ({ ...p, question_text: e.target.value }))}
                                              className="mt-2 w-full rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-3 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:bg-white"
                                              placeholder="Untitled Question"
                                            />
                                          </div>

                                          <div className="w-full sm:w-[240px]">
                                            <label className="block text-xs font-black text-gray-700 uppercase tracking-[0.18em]">Type</label>
                                            <select
                                              value={qDraft.type}
                                              onChange={(e) => {
                                                const next = e.target.value;
                                                setQDraft((p) => ({
                                                  ...p,
                                                  type: next,
                                                  options: next === 'text' ? [] : (p.options?.length ? p.options : ['', '']),
                                                }));
                                              }}
                                              className="mt-2 w-full rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:bg-white"
                                            >
                                              <option value="single_choice">Multiple choice</option>
                                              <option value="multi_choice">Checkboxes</option>
                                              <option value="text">Paragraph</option>
                                            </select>
                                          </div>
                                        </div>

                                        {qDraft.type !== 'text' ? (
                                          <div className="mt-5">
                                            <div className="text-xs font-black text-gray-700 uppercase tracking-[0.18em]">Options</div>
                                            <div className="mt-3 space-y-2">
                                              {(qDraft.options || []).map((opt, i2) => (
                                                <div key={i2} className="flex items-center gap-3">
                                                  <div className="w-5 h-5 flex items-center justify-center text-gray-300">
                                                    <Icon
                                                      icon={qDraft.type === 'multi_choice' ? 'fluent:checkbox-unchecked-24-regular' : 'fluent:radio-button-24-regular'}
                                                      width={18}
                                                      height={18}
                                                    />
                                                  </div>
                                                  <input
                                                    value={opt}
                                                    onChange={(e) => {
                                                      const v = e.target.value;
                                                      setQDraft((p) => {
                                                        const next = [...(p.options || [])];
                                                        next[i2] = v;
                                                        return { ...p, options: next };
                                                      });
                                                    }}
                                                    className="flex-1 w-full rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-2.5 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:bg-white"
                                                    placeholder={`Option ${i2 + 1}`}
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={async () => {
                                                      const nextOptions = [...(qDraft.options || [])];
                                                      nextOptions.splice(i2, 1);
                                                      const finalOptions = nextOptions.length ? nextOptions : ['Option 1', 'Option 2'];
                                                      
                                                      setQDraft((p) => ({ ...p, options: finalOptions }));
                                                      
                                                      // If existing question, save immediately as per user request
                                                      if (activeQid && !String(activeQid).startsWith('temp-')) {
                                                        try {
                                                          const payload = {
                                                            question_text: qDraft.question_text,
                                                            type: qDraft.type,
                                                            is_required: qDraft.is_required ? 1 : 0,
                                                            options: finalOptions
                                                          };
                                                          await updateGenericSurveyQuestion(surveyId, activeQid, payload);
                                                          // Sync local questions so the list view matches
                                                          syncDraftToLocalQuestions();
                                                          await fetchSurvey(true);
                                                        } catch (e) {}
                                                      } else {
                                                        // Just sync locally for unsaved questions
                                                        setQuestions(prev => prev.map(q => q.id === activeQid ? { ...q, options: finalOptions.map(o => ({ option_text: o })) } : q));
                                                      }
                                                    }}
                                                    className="w-10 h-10 rounded-2xl border border-gray-100 bg-white text-gray-400 hover:text-red-500 hover:bg-red-50 transition flex items-center justify-center"
                                                    title="Remove option"
                                                  >
                                                    <Icon icon="fluent:delete-24-regular" width={18} height={18} />
                                                  </button>
                                                </div>
                                              ))}

                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setQDraft((p) => ({ ...p, options: [...(p.options || []), ''] }));
                                                }}
                                                className="inline-flex items-center gap-2 rounded-2xl border border-gray-100 bg-white px-4 py-2.5 text-sm font-black text-gray-700 hover:bg-gray-50"
                                              >
                                                <Icon icon="fluent:add-24-regular" width={18} height={18} className="text-[#7083FF]" />
                                                Add option
                                              </button>
                                            </div>
                                          </div>
                                        ) : null}

                                        <div className="mt-5 flex items-center justify-between gap-3">
                                          <label className="inline-flex items-center gap-2 text-sm font-black text-gray-700">
                                            <span>Required</span>
                                            <span className="relative inline-flex items-center cursor-pointer select-none">
                                              <input
                                                type="checkbox"
                                                checked={qDraft.is_required}
                                                onChange={(e) => setQDraft((p) => ({ ...p, is_required: e.target.checked }))}
                                                className="sr-only"
                                              />
                                              <span className={`w-9 h-5 rounded-full transition-colors ${qDraft.is_required ? 'bg-[#7083FF]' : 'bg-gray-200'}`} />
                                              <span className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${qDraft.is_required ? 'translate-x-4' : 'translate-x-0'}`} />
                                            </span>
                                          </label>
                                          <div className="mt-5 flex items-center justify-end gap-3">
                                            <button
                                              type="button"
                                              onClick={handleCloseEditor}
                                              className="px-4 py-2 text-sm font-black text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-2xl transition"
                                            >
                                              Close
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </StrictModeDroppable>
                </DragDropContext>
              )}

              <div className="pt-2">
                <button
                  type="button"
                  onClick={openNewQuestion}
                  className="w-full rounded-[24px] border border-dashed border-gray-200 bg-gray-50/30 px-5 py-4 text-left hover:bg-gray-50 transition"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-black text-gray-900">Add a question</div>
                      <div className="text-xs text-gray-500 mt-0.5">Create a new question at the end of the survey</div>
                    </div>
                    <div className="w-10 h-10 rounded-2xl bg-[#7083FF] flex items-center justify-center text-white shadow-md shadow-[#7083FF]/20 ring-4 ring-[#7083FF]/10">
                      <Icon icon="fluent:add-24-filled" width={22} height={22} />
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminPage>
  );
}

export default SurveyBuilder;
